import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import WorkCard from '@/components/feature/WorkCard';
import { useToast } from '@/components/feature/ToastProvider';
import { useFavorites } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';
import { latestWorks, works } from '@/mocks/catalog';

export default function FavoritesPage() {
  const { favorites, toggleFavorite } = useFavorites();
  const { showToast } = useToast();

  useSeo({
    title: '我的收藏 - 91大神影视平台',
    description: '查看你在 91大神影视平台收藏的电影、剧集与综艺。收藏保存在浏览器本地，刷新页面后依然保留。',
    keywords: '91大神, 我的收藏, 影视片单, 本地收藏',
    canonicalPath: '/favorites',
  });

  const saved = favorites
    .map((id) => works.find((work) => work.id === id))
    .filter((work): work is NonNullable<typeof work> => Boolean(work));

  const clearAll = () => {
    saved.forEach((work) => toggleFavorite(work.id));
    showToast('已清空本地收藏', 'info');
  };

  const recommendations = latestWorks.filter((work) => !favorites.includes(work.id)).slice(0, 5);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-accent-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-800">我的收藏</span>
        </nav>

        <div className="mt-4 flex flex-wrap items-end justify-between gap-3 rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
          <div>
            <h1 className="font-display text-xl font-bold text-foreground-950 md:text-2xl">我的收藏</h1>
            <p className="mt-1.5 text-sm text-foreground-500">
              共 <span className="font-semibold text-accent-700">{saved.length}</span> 部作品 · 收藏保存在当前浏览器本地
            </p>
          </div>
          {saved.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300/70 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-700"
            >
              <i className="ri-delete-bin-6-line"></i>
              清空收藏
            </button>
          )}
        </div>

        {saved.length === 0 ? (
          <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-heart-3-line text-xl"></i>
            </span>
            <p className="mt-4 text-base font-semibold text-foreground-950">你的片单还是空的</p>
            <p className="mt-1.5 max-w-md text-sm text-foreground-500">
              在任意作品详情页点击「加入收藏」，作品就会出现在这里，刷新页面也不会丢失。
            </p>
            <Link
              to="/category/movie"
              className="mt-5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400"
            >
              去挑选作品
            </Link>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {saved.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        )}

        <section className="mt-10">
          <h2 className="font-display text-lg font-bold text-foreground-950">为你推荐</h2>
          <p className="mt-1 text-xs text-foreground-500">近期上线的热门作品</p>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {recommendations.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}